<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/shared/analytics'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import {
  BRADLEY_ANALYTICS_EVENTS,
  BRADLEY_ICEBREAKER_COUNT,
  BRADLEY_ICEBREAKER_IDS,
} from '../analytics/bradley-analytics.constants.js'

const emit = defineEmits(['selectPrompt'])

const { t } = useI18n()
const { trackEvent } = useAnalytics()
const { dataAnalysisEnabled: DATA_ANALYSIS_ENABLED } = useBradleyEnvironment()

const sectionReference = ref(null)
const impressionTracked = ref(false)
let impressionObserver = null

const promptSuggestions = BRADLEY_ICEBREAKER_IDS.map(id => ({
  id,
  icon: 'pi pi-sparkles',
}))

function resolvePromptI18nKey(id) {
  if (id === 'prompt2' && !DATA_ANALYSIS_ENABLED) {
    return 'prompt2Alt'
  }
  return id
}

function trackImpression() {
  if (impressionTracked.value) {
    return
  }
  impressionTracked.value = true
  trackEvent(BRADLEY_ANALYTICS_EVENTS.ICEBREAKER_IMPRESSION, {
    total_icebreakers: BRADLEY_ICEBREAKER_COUNT,
    visible_positions: BRADLEY_ICEBREAKER_IDS.map((_, index) => index + 1),
    source_component: 'icebreaker_section',
  })
}

function setupImpressionObserver() {
  if (impressionTracked.value || typeof IntersectionObserver === 'undefined') {
    return
  }
  const element = sectionReference.value
  if (!element) {
    return
  }
  impressionObserver = new IntersectionObserver((entries) => {
    if (entries.some(entry => entry.isIntersecting)) {
      trackImpression()
      impressionObserver?.disconnect()
      impressionObserver = null
    }
  }, { threshold: 0.25 })
  impressionObserver.observe(element)
}

onMounted(() => {
  setupImpressionObserver()
})

onBeforeUnmount(() => {
  impressionObserver?.disconnect()
  impressionObserver = null
})

function handlePromptClick(icebreakerId, position) {
  const promptText = t(`newBradley.promptCards.${resolvePromptI18nKey(icebreakerId)}`)
  emit('selectPrompt', {
    text: promptText,
    icebreakerId,
    icebreakerPosition: position,
    icebreakerText: promptText,
  })
}
</script>

<template>
  <section
    ref="sectionReference"
    class="bradley-prompt-cards grid grid-cols-1 md:grid-cols-2 gap-3"
    aria-label="Suggested prompts"
  >
    <button
      v-for="(prompt, index) in promptSuggestions"
      :key="prompt.id"
      type="button"
      class="bg-white border border-dss-surface-200 rounded-2xl py-2 px-[14px] text-left hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
      @click="handlePromptClick(prompt.id, index + 1)"
    >
      <div class="flex items-center gap-3">
        <i
          :class="prompt.icon"
          class="text-[10.5px] leading-none text-gray-700 flex-shrink-0"
        />
        <span class="font-inter text-xs font-normal leading-snug text-gray-700 group-hover:text-gray-900">
          {{ $t(`newBradley.promptCards.${resolvePromptI18nKey(prompt.id)}`) }}
        </span>
      </div>
    </button>
  </section>
</template>
